import { CLIENT_IMPORT_FIELDS, ClientImportField } from "./clientImportFields";
import {
    ColumnSuggestion as GenericColumnSuggestion,
    MappingConfidence,
    createMappingSuggester,
    normalizeHeader,
    sampleMatches,
} from "./core/suggestMapping";

export { normalizeHeader };
export type { MappingConfidence };

export type ColumnSuggestion = GenericColumnSuggestion & {
    field: ClientImportField["name"] | null;
};

/**
 * Sinonimos por atributo. Es una ayuda para adivinar mejor, NO una lista de encabezados
 * requeridos: los archivos vienen de sistemas externos y las columnas pueden llamarse
 * como sea. Ampliar esta tabla con cada archivo real que aparezca es barato y seguro.
 */
const FIELD_SYNONYMS: Record<string, string[]> = {
    nombre: ["nombre", "cliente", "razon social", "nombre cliente", "nombre completo", "descripcion"],
    nombre_comercial: ["nombre comercial", "comercial", "nombre negocio", "nombre del negocio", "razon comercial", "alias", "fantasia", "nombre fantasia"],
    codigo: ["codigo", "cod", "clave", "numero cliente", "no cliente", "id cliente", "referencia"],
    identificacion: [
        "identificacion",
        "cedula",
        "ced",
        "cedula juridica",
        "cedula fisica",
        "id",
        "nit",
        "ruc",
        "rfc",
        "dni",
        "documento",
        "cif",
    ],
    email: ["email", "correo", "correo electronico", "e mail", "mail", "correos"],
    telefono1: ["telefono", "tel", "telefono 1", "celular", "movil", "contacto telefonico", "numero telefonico"],
    telefono2: ["telefono 2", "tel 2", "telefono alterno", "celular 2", "otro telefono", "fax"],
    telefono1_cd: ["codigo pais", "codigo de pais", "cod pais", "prefijo", "lada"],
    telefono2_cd: ["codigo pais 2", "codigo de pais 2", "prefijo 2"],
    direccion: ["direccion", "domicilio", "senas", "ubicacion", "direccion exacta"],
    zoneId: ["zona", "territorio", "ruta", "sector", "region", "distrito comercial"],
    credit_limit: ["limite de credito", "limite credito", "cupo", "cupo de credito", "credito maximo"],
    plazo: ["plazo", "plazo de credito", "dias credito", "termino de pago", "condicion de pago"],
    descuento: ["descuento", "porcentaje descuento", "desc", "pct descuento"],
    activitycode: ["codigo de actividad", "actividad economica", "codigo actividad", "ciiu", "actividad"],
    contacto: ["contacto", "nombre de contacto", "persona contacto", "encargado"],
    active: ["activo", "estado", "habilitado", "vigente"],
    tipo: ["tipo", "tipo cliente", "credito contado", "condicion", "modalidad"],
    cuentas_bancarias: ["cuentas bancarias", "cuenta bancaria", "banco", "iban"],
    einvoice_kind: ["tipo de factura", "tipo factura electronica", "tipo documento"],
};

const DIGITS_ONLY = /^\d+$/;

/**
 * Deduce el atributo mirando los valores de la columna.
 *
 * Es la unica senal disponible cuando el archivo no trae encabezados, y sirve de
 * confirmacion cuando si los trae.
 */
const detectByContent = (sample: string[]): ClientImportField["name"] | null => {
    const matches = (predicate: (value: string) => boolean): boolean => sampleMatches(sample, predicate);

    if (matches((value) => value.includes("@") && value.includes("."))) return "email";

    // `normalizeHeader` tambien sirve aqui: quita la tilde de "Si" y baja a minusculas.
    if (matches((value) => /^(si|no|true|false|s|n|x|1|0)$/.test(normalizeHeader(value)))) return "active";

    const digits = (value: string): string => value.replace(/[\s-]/g, "");

    if (matches((value) => DIGITS_ONLY.test(digits(value)) && digits(value).length === 8)) return "telefono1";

    if (
        matches((value) => {
            const clean = digits(value);
            return DIGITS_ONLY.test(clean) && clean.length >= 9 && clean.length <= 12;
        })
    ) {
        return "identificacion";
    }

    // Codigos de actividad de Hacienda: 4 a 6 digitos, con un decimal opcional.
    if (matches((value) => /^\d{4,6}([.,]\d)?$/.test(value))) return "activitycode";

    return null;
};

const suggester = createMappingSuggester({
    fields: CLIENT_IMPORT_FIELDS,
    synonyms: FIELD_SYNONYMS,
    detectByContent,
});

/** Mejor atributo para un nombre de columna, o `null` si ninguno destaca lo suficiente. */
export const suggestFieldByName = suggester.suggestFieldByName as (
    header: string
) => ClientImportField["name"] | null;

export const suggestFieldByContent = suggester.suggestFieldByContent as (
    values: string[]
) => ClientImportField["name"] | null;

/** Propone un mapeo columna -> atributo combinando el nombre de la columna y su contenido. */
export const suggestColumnMapping = suggester.suggestColumnSuggestions as (
    columnNames: string[],
    dataRows: string[][]
) => ColumnSuggestion[];

export const suggestClientColumnMapping = suggester.suggestColumnMapping;
